import { useSearchParams } from 'react-router-dom';
import { useTranslate } from 'react-admin';
import { Alert, Button, Stack, Tab, Tabs } from '@mui/material';

import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { Iconify } from 'src/components/iconify';
import { DashboardContent } from 'src/layouts/dashboard';
import LoadingPanel from 'src/pages/admin/components/LoadingPanel';
import ErrorPage403 from 'src/pages/error/403';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import {
  VaultCoreParams,
  VaultRiskParams,
  updateVaultCoreParams,
  updateVaultRiskParams,
} from 'src/pages/admin/services/vaultParamsService';

import { VAULT_PARAMS_NS as NS, VAULT_PARAMS_PERMISSION_RESOURCE } from './constants';
import { DerivedParamsCard } from './DerivedParamsCard';
import { CORE_FIELDS, RISK_FIELDS } from './fields';
import { validateCoreDraft, validateRiskDraft } from './helpers';
import { ParamsCard } from './ParamsCard';
import { useParamsEditor, useVaultParams } from './useVaultParams';

type TabValue = 'core' | 'risk';

const EMPTY_ENTRY = { value: null, revision: 0, isSet: false };

const VaultParamsList = () => {
  const translate = useTranslate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Each tab has its own URL (PRD §14.1): ?tab=core | ?tab=risk.
  const tab: TabValue = searchParams.get('tab') === 'risk' ? 'risk' : 'core';

  const { params, loading, loadError, refetch } = useVaultParams();

  const coreEditor = useParamsEditor<VaultCoreParams>({
    entry: params?.core ?? EMPTY_ENTRY,
    defaults: params?.coreDefaults ?? null,
    fields: CORE_FIELDS,
    validate: validateCoreDraft,
    save: updateVaultCoreParams,
    refetch,
    revisionOf: (p) => p.core.revision,
  });

  const riskEditor = useParamsEditor<VaultRiskParams>({
    entry: params?.risk ?? EMPTY_ENTRY,
    defaults: params?.riskDefaults ?? null,
    fields: RISK_FIELDS,
    validate: validateRiskDraft,
    save: updateVaultRiskParams,
    refetch,
    revisionOf: (p) => p.risk.revision,
  });

  return (
    <PermissionGuard
      resource={VAULT_PARAMS_PERMISSION_RESOURCE}
      action="read"
      fallback={<ErrorPage403 />}
    >
      <DashboardContent>
        <CustomBreadcrumbs
          heading={translate(`${NS}.title`)}
          links={[
            { name: translate('resource.vault'), href: '#' },
            { name: translate(`${NS}.title`) },
          ]}
          action={
            <Button
              variant="outlined"
              startIcon={<Iconify icon="solar:refresh-bold" />}
              onClick={() => refetch()}
              disabled={loading}
            >
              {translate(`${NS}.refresh`)}
            </Button>
          }
        />

        <Tabs
          value={tab}
          onChange={(_, next: TabValue) => setSearchParams({ tab: next }, { replace: true })}
          variant="scrollable"
          allowScrollButtonsMobile
          sx={{ mt: 2 }}
        >
          <Tab value="core" label={translate(`${NS}.tabs.core`)} />
          <Tab value="risk" label={translate(`${NS}.tabs.risk`)} />
        </Tabs>

        <LoadingPanel isLoading={loading}>
          <Stack spacing={3} sx={{ mt: 3 }}>
            <Alert severity="warning">{translate(`${NS}.liveWriteHint`)}</Alert>
            {loadError && <Alert severity="error">{loadError}</Alert>}

            {params && tab === 'core' && (
              <>
                <ParamsCard<VaultCoreParams>
                  title={`${NS}.core.title`}
                  subtitle={`${NS}.core.subtitle`}
                  modalTitle={`${NS}.core.modalTitle`}
                  fields={CORE_FIELDS}
                  entry={params.core}
                  defaults={params.coreDefaults}
                  editor={coreEditor}
                  historyRecordId="core"
                />
                <DerivedParamsCard derived={params.derived} />
              </>
            )}

            {params && tab === 'risk' && (
              <ParamsCard<VaultRiskParams>
                title={`${NS}.risk.title`}
                subtitle={`${NS}.risk.subtitle`}
                modalTitle={`${NS}.risk.modalTitle`}
                fields={RISK_FIELDS}
                entry={params.risk}
                defaults={params.riskDefaults}
                editor={riskEditor}
                historyRecordId="risk"
              />
            )}
          </Stack>
        </LoadingPanel>
      </DashboardContent>
    </PermissionGuard>
  );
};

export default VaultParamsList;
